// React Imports
import React, { useState } from "react";

// 3rd Party Imports
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

// Hooks & Helpers
import {
  generateScormPackage,
  type ScormLesson,
} from "../../../helpers/scorm/generateScormPackage";
import { useToast } from "../../../hooks/ToastContext";

const EXPORT_SCORM = "Export to SCORM";
const SCORM_MESSAGES = {
  singleLesson:
    "Export a lesson to a SCORM package so it can be added to your internal LMS. Learners can then be assigned Security Journey training directly from your LMS.",
  multipleLessons:
    "Export all lessons in this path to a SCORM package so it can be added to your internal LMS. Learners can then be assigned Security Journey training directly from your LMS.",
  note: "Note: A single zip file will be generated, containing a zip file for each lesson within the path.",
};

export type ScormExportDialogProps = {
  visible: boolean;
  onHide: () => void;
  lessons: ScormLesson[];
};

export const ScormExportDialog: React.FC<ScormExportDialogProps> = ({
  visible,
  onHide,
  lessons,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const isMultipleLessons = lessons.length > 1;
  const { showToast } = useToast();

  const handleGenerate = async () => {
    setIsGenerating(true);

    try {
      await generateScormPackage({ lessons });
      showToast({
        severity: "success",
        summary: "Files Published!",
        detail:
          "SCORM file packed and ready! Your lessons are now securely stowed in your digital backpack, prepped for the next leg of your Security Journey\u2014happy travels on the learning trail!",
        life: 3000,
      });
    } catch (error) {
      console.error("Error exporting SCORM", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail:
          "Failed to export lessons: An unexpected error has occured.  Please refresh the page and try again",
        life: 3000,
      });
    } finally {
      setIsGenerating(false);
      onHide();
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={EXPORT_SCORM}
      modal
      style={{ width: "525px" }}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", padding: "1rem" }}
        >
          <Button outlined label="Cancel" onClick={onHide} disabled={isGenerating} />
          <Button autoFocus label="Generate" onClick={handleGenerate} loading={isGenerating} />
        </div>
      }
    >
      <p className={`${isMultipleLessons ? "mb-4" : ""}`}>
        {isMultipleLessons ? SCORM_MESSAGES.multipleLessons : SCORM_MESSAGES.singleLesson}
      </p>

      {isMultipleLessons && <p className="text-secondary text-sm">{SCORM_MESSAGES.note}</p>}
    </Dialog>
  );
};
